import React, { createContext, useEffect, useState } from 'react'
import { useMatchupUrlParams } from '../utils'
import {
  MatchupInfo,
  LeaderData,
  TeamGQL,
  PlayerDataGQL,
  fetchMatchupInfoBySlugGQL,
  EventGQL,
  getTeamAndPlayerInfoStatsGQL,
  TeamStanding,
  KeyMatchup,
  fetchActiveBanners,
  Banner,
  FeedItem,
  fetchStandings,
  getFeedByTag,
} from '../../Api'
import { STATS_YEAR, CURRENT_YEAR } from '../../Config/League/league.config'
import { getAdditionalTeamData, SEASON_SEGMENT_NAMES } from '../../Utils'
import { playersToWatchList } from '../../Constant/player'

export type MatchupTeam = {
  teamId: string
  data: TeamGQL
  status?: string
}

type MatchupContextType = {
  officialId: string
  isLoading: boolean
  eventInfo: EventGQL | null
  infoYear: number | null
  statYear: number | null
  matchupInfo: MatchupInfo | null
  teams: MatchupTeam[]
  keyPlayerMatchup: KeyMatchup[]
  homePlayers: PlayerDataGQL[]
  awayPlayers: PlayerDataGQL[]
  statLeaders: LeaderData[]
  standings: TeamStanding[]
  gameFeed: FeedItem[]
  banners: Banner | null
  fetchGameFeed: (includeVideos: boolean, limit?: number) => Promise<FeedItem[]>
  getStandings: (year: number, isChampSeriesSeg: boolean) => Promise<TeamStanding[]>
}

export const MatchupContext = createContext<MatchupContextType | null>(null)

export const MatchupProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const route = useMatchupUrlParams()
  const officialId = route?.officialId ?? ''
  const [isLoading, setIsLoading] = useState(true)
  const [eventInfo, setEventInfo] = useState<EventGQL | null>(null)
  const [matchupInfo, setMatchupInfo] = useState<MatchupInfo | null>(null)
  const [infoYear, setInfoYear] = useState<number | null>(null)
  const [statYear, setStatYear] = useState<number | null>(null)
  const [teams, setTeams] = useState<MatchupTeam[]>([])
  const [keyPlayerMatchup, setKeyPlayerMatchup] = useState<KeyMatchup[]>([])
  const [homePlayers, setHomePlayers] = useState<PlayerDataGQL[]>([])
  const [awayPlayers, setAwayPlayers] = useState<PlayerDataGQL[]>([])
  const [statLeaders, setStatLeaders] = useState<LeaderData[]>([])
  const [standings, setStandings] = useState<TeamStanding[]>([])
  const [gameFeed, setGameFeed] = useState<FeedItem[]>([])
  const [banners, setBanners] = useState<Banner | null>(null)

  useEffect(() => {
    if (officialId) {
      fetchData()
    }
  }, [officialId])

  const fetchData = async () => {
    try {
      await Promise.all([fetchEventInfo(), fetchBanners()])
    } catch (err) {
      console.error(err)
      setIsLoading(false)
    }
  }

  const setYears = () => {
    if (statYear && infoYear) {
      return { infoYear, statYear }
    }

    let newInfoYear: number
    let newStatYear: number

    if (!eventInfo?.year) {
      newInfoYear = CURRENT_YEAR
      newStatYear = STATS_YEAR
    } else {
      newInfoYear = eventInfo.year
      newStatYear = eventInfo.week === '1' ? eventInfo.year - 1 : eventInfo.year
    }

    setInfoYear(newInfoYear)
    setStatYear(newStatYear)
    return { infoYear: newInfoYear, statYear: newStatYear }
  }

  const fetchEventInfo = async () => {
    try {
      const matchupEvent = await fetchMatchupInfoBySlugGQL(
        officialId,
        playersToWatchList
      )
      if (!matchupEvent) return
      setEventInfo(matchupEvent)

      const { infoYear, statYear } = setYears()
      const segment = matchupEvent.seasonSegment

      const [homeTeam, awayTeam] = await Promise.all([
        getTeamAndPlayerInfoStatsGQL(
          matchupEvent.homeTeam.officialId,
          infoYear,
          SEASON_SEGMENT_NAMES.regular,
          statYear,
          segment === SEASON_SEGMENT_NAMES.champseries
        ),
        getTeamAndPlayerInfoStatsGQL(
          matchupEvent.awayTeam.officialId,
          infoYear,
          SEASON_SEGMENT_NAMES.regular,
          statYear,
          segment === SEASON_SEGMENT_NAMES.champseries
        ),
      ])

      if (homeTeam && awayTeam) {
        setupTeamsAndPlayers(matchupEvent, awayTeam, homeTeam)
        createStatLeaders(matchupEvent)
        if (matchupEvent?.matchupInfo) {
          setMatchupInfo(matchupEvent.matchupInfo)
          createKeyMatchup(matchupEvent.matchupInfo)
        }
      }
      setIsLoading(false)
    } catch (err) {
      setIsLoading(false)
    }
  }

  const fetchBanners = async () => {
    try {
      const bannerData = await fetchActiveBanners(true, 'matchup')
      setBanners(bannerData[0])
    } catch (err) {
      console.error('Error fetching banners:', err)
    }
  }

  const getStandings = async (year: number, isChampSeriesSeg: boolean) => {
    try {
      const teams = await fetchStandings(year, isChampSeriesSeg)
      if (!teams) return []
      setStandings(teams)
      return teams
    } catch (e) {
      console.log(e)
      return []
    }
  }

  const fetchGameFeed = async (includeVideos: boolean = false, limit?: number) => {
    const feedParams = {
      tags: [officialId],
      includeVideos,
      limit,
    }
    try {
      const feed = await getFeedByTag(feedParams)
      if (!feed) return []
      setGameFeed(feed)
      return feed.length > 0 ? feed : []
    } catch (err) {
      console.log(err)
      return []
    }
  }

  const createStatLeaders = (eventInfo: EventGQL) => {
    let away: LeaderData[] = []
    let home: LeaderData[] = []
    let awayLeaders =
      eventInfo?.seasonSegment === SEASON_SEGMENT_NAMES.champseries
        ? eventInfo?.awayTeam?.csStatLeaders
        : eventInfo?.awayTeam?.statLeaders
    let homeLeaders =
      eventInfo?.seasonSegment === SEASON_SEGMENT_NAMES.champseries
        ? eventInfo?.homeTeam?.csStatLeaders
        : eventInfo?.homeTeam?.statLeaders
    if (awayLeaders && awayLeaders.length > 0) {
      away = awayLeaders
    }
    if (homeLeaders && homeLeaders.length > 0) {
      home = homeLeaders
    }

    setStatLeaders([...away, ...home])
  }

  const createKeyMatchup = (matchupInfo: MatchupInfo) => {
    if (matchupInfo?.keyMatchup && matchupInfo.keyMatchup.length > 0) {
      setKeyPlayerMatchup(matchupInfo.keyMatchup)
    }
  }

  const setupTeamsAndPlayers = (event: EventGQL, away: TeamGQL, home: TeamGQL) => {
    const { awayTeam, homeTeam } =
      event?.seasonSegment === SEASON_SEGMENT_NAMES.champseries
        ? mergeChampSeriesTeamData(away, home)
        : mergeTeamData(away, home)

    setTeams([awayTeam, homeTeam])
    setAwayPlayers(awayTeam.data.players || [])
    setHomePlayers(homeTeam.data.players || [])
  }

  const mergeTeamData = (away: TeamGQL, home: TeamGQL) => {
    const homeData = { ...home, ...getAdditionalTeamData(home.officialId) }
    const awayData = { ...away, ...getAdditionalTeamData(away.officialId) }

    const awayTeam = {
      teamId: away.officialId,
      status: 'away',
      data: awayData,
    }

    const homeTeam = {
      teamId: home.officialId,
      status: 'home',
      data: homeData,
    }

    return { awayTeam, homeTeam }
  }

  // CHAMP SERIES //

  const mergeChampSeriesTeamData = (away: TeamGQL, home: TeamGQL) => {
    let awayT = { ...away, ...getAdditionalTeamData(away.officialId) }
    let homeT = { ...home, ...getAdditionalTeamData(home.officialId) }
    let updatedAway = updateChampSeriesRoster(awayT)
    let updatedHome = updateChampSeriesRoster(homeT)
    const awayTeam = {
      teamId: away.officialId,
      status: 'away',
      data: updatedAway,
    }
    const homeTeam = {
      teamId: home.officialId,
      status: 'home',
      data: updatedHome,
    }

    return { awayTeam, homeTeam }
  }

  const updateChampSeriesRoster = (team: TeamGQL) => {
    let playerList = team.champSeries?.roster ?? []
    let players: PlayerDataGQL[] = []
    if (playerList.length > 0) {
      playerList.map((player) => {
        if (!player?.champSeries) return
        player.position = player.champSeries?.position
        player.positionName = player.champSeries?.positionName
        player.stats = player.champSeries?.stats || {}
        players.push(player)
        return
      })
    }
    team.players = players
    return team
  }

  const contextValue: MatchupContextType = {
    officialId,
    isLoading,
    eventInfo,
    infoYear,
    statYear,
    matchupInfo,
    teams,
    keyPlayerMatchup,
    homePlayers,
    awayPlayers,
    statLeaders,
    standings,
    gameFeed,
    banners,
    fetchGameFeed,
    getStandings,
  }

  return (
    <MatchupContext.Provider value={contextValue}>{children}</MatchupContext.Provider>
  )
}
